import React, { Fragment } from 'react';

import t from '../../../labels/labelling-utils';
import { CompetitionClassification, competitionClassificationList } from '../../../typings/competition';
import { UEFACompetitionQualification } from '../../../utils/competition-qualification';
import { placeholdersMap, TEAMLOGO } from '../../../utils/placeholders-football';
import { getNewUefaFormatLabels, uefaCompetitionsId } from '../../../utils/ranking-legends';
import Cta from '../../atoms/cta/cta';
import ImgPlaceholder from '../../atoms/img-placeholder/img-placeholder';
import MatchTrend, { IMatchTrendItem } from '../../molecules/match-trend/match-trend';
import Table, { IColumn, IRow } from '../../molecules/table/table';

import './competition-ranking-table.scss';

export type CompetitionTrend = 'up' | 'static' | 'down';
export const CompetitionTrendList: CompetitionTrend[] = ['up', 'static', 'down'];

export const COMPETITION_RANKING_LAST_MATCH_TREND_LIMIT = 5;

export type CompetitionHighlight = UEFACompetitionQualification | CompetitionClassification | '' | null | undefined;

export const CompetitionHighlightList: CompetitionHighlight[] = [...competitionClassificationList, '', null, undefined];

/**
 * ICompetitionRankingRow interface
 * @param highlight - ENUM to show a COLORED VERTICAL BAR at the left of the row
 * @param lastMatchesTrend - An ARRAY of OBJECTS with the latest 5 matches results
 * @param logoLink - A computed <img src> React Element for showing TEAM LOGO
 * @param teamLink - A computed <a href> React Element for clickable TEAM PAGE
 * @param trend - ENUM to show a COLORED DOT at the left of team logo
 */
export interface ICompetitionRankingRow extends IRow {
  games: number;
  gamesDraw: number;
  gamesLost: number;
  gamesWon: number;
  goalsConceded: number;
  goalsDifference: number;
  goalsScored: number;
  id: string; // FIXME: should/will be renamed teamId
  lastMatchesTrend?: object[] | null;
  logoLink?: JSX.Element | null; // NEW field (generated with MAPPING @ line #117)
  logoUrl: string;
  points: number;
  position: number;
  qualification?: CompetitionHighlight;
  roundName?: string;
  teamLink?: JSX.Element | null; // NEW field (generated with MAPPING @ line #117)
  teamName: string;
  teamUrl?: string | null;
  trend: CompetitionTrend;
}

/**
 * ICompetitionRankingTable interface
 * @param className - The name used to create dynamic CSS class names
 * @param pagination - An OBJECT returned from API to manage PAGINATION (still TBD)
 * @param rows - ARRAY of OBJECTS data rows
 * @param showTitles - Boolean to show/hide THEAD
 * @param team - Array of TEAM NAMES to be SELECTED
 * @param teamId - TEAM ID to be SELECTED (for new TEAM RANKINGS template)
 */
export interface ICompetitionRankingTable {
  className?: string;
  pagination?: object;
  rows: Array<ICompetitionRankingRow>;
  showTitles?: boolean;
  team?: Array<string>;
  teamId?: string;
  showLastMatchTrend?: boolean;
  isSummaryVersion?: boolean;
  trackingData?: object;
  competitionId: string | number;
}

const getColumns = (isSummaryVersion?: boolean): IColumn[] => [
  {
    title: `${t('competition.ranking.th.position')}`,
    data: 'position',
    classModifier: 'position',
    classColumn: 'qualification',
  },
  {
    title: `${t('competition.ranking.th.logo')}`, // ISS-4676 - SEO do not allow for EMPTY TH
    data: 'logoLink', // NEW field (generated with MAPPING @ line #117)
    colspan: 1,
    classModifier: 'logo',
    classColumn: 'trend',
  },
  {
    title: `${t('competition.ranking.th.team')}`,
    data: 'teamLink', // NEW field (generated with MAPPING @ line #117)
    colspan: 2,
    classModifier: isSummaryVersion ? 'team-summary' : 'team',
  },
  {
    title: `${t('competition.ranking.th.points')}`,
    data: 'points',
    classModifier: 'points',
  },
  {
    title: `${t('competition.ranking.th.games')}`,
    data: 'games',
    classModifier: 'mobile-show',
  },
  {
    title: `${t('competition.ranking.th.games.won')}`,
    data: 'gamesWon',
    classModifier: 'mobile-hide',
  },
  {
    title: `${t('competition.ranking.th.games.draw')}`,
    data: 'gamesDraw',
    classModifier: 'mobile-hide',
  },
  {
    title: `${t('competition.ranking.th.games.lost')}`,
    data: 'gamesLost',
    classModifier: 'mobile-hide',
  },
  {
    title: `${t('competition.ranking.th.goals.scored')}`,
    data: 'goalsScored',
    classModifier: 'mobile-hide',
  },
  {
    title: `${t('competition.ranking.th.goals.conceded')}`,
    data: 'goalsConceded',
    classModifier: 'mobile-hide',
  },
  {
    title: `${t('competition.ranking.th.goals.difference')}`,
    data: 'goalsDifference',
    classModifier: 'mobile-show',
  },
];

/**
 * Renders a TABLE component passing an ARRAY of ROWS
 * @param className - The name used to create dynamic CSS class names
 * @param pagination - An OBJECT returned from API to manage PAGINATION (still TBD)
 * @param rows - ARRAY of OBJECTS data rows
 * @param showTitles - Boolean to show/hide THEAD
 * @param team - Array of TEAM NAMES to be SELECTED
 * @param teamId - TEAM ID to be SELECTED (for new TEAM RANKINGS template)
 */
const CompetitionRankingTable = ({
  /* istanbul ignore next: default value */ className = 'competition-ranking',
  /* istanbul ignore next: default value */ pagination = {},
  /* istanbul ignore next: default value */ rows = [],
  /* istanbul ignore next: default value */ showTitles = true,
  isSummaryVersion,
  team,
  teamId,
  competitionId,
  /* istanbul ignore next: default value */ showLastMatchTrend = true,
  trackingData,
}: ICompetitionRankingTable) => {
  const _columns = getColumns(isSummaryVersion);

  // TODO: maybe we need to add a minimum elements treshold in lastMatchesTrend to display e.g. 3 on
  if (showLastMatchTrend && !isSummaryVersion && rows.length && rows[0].hasOwnProperty('lastMatchesTrend')) {
    _columns.push({
      title: `${t('competition.ranking.th.trend')}`,
      data: 'lastMatchesTrend',
      classModifier: 'last-matches-trend',
      render: /* istanbul ignore next: tested in <Table/> component */ (props: IMatchTrendItem[]) => (
        <MatchTrend lastMatchesTrend={props} limit={COMPETITION_RANKING_LAST_MATCH_TREND_LIMIT} />
      ),
    });
  }

  const getQualification = (qualification: CompetitionHighlight) => {
    return qualification && uefaCompetitionsId.includes(competitionId.toString())
      ? getNewUefaFormatLabels[qualification].color
      : qualification;
  };

  const columns = isSummaryVersion ? _columns.filter((colum) => colum.classModifier !== 'mobile-hide') : _columns;

  // COMPUTE ROWS PROPS
  const _rows: Array<ICompetitionRankingRow> = rows.map((row: ICompetitionRankingRow, index: number) => {
    // eslint-disable-next-line no-console
    console.log(`index: ${index}`, row.qualification);

    const item: ICompetitionRankingRow = {
      ...row,
      qualification: getQualification(row.qualification),
      logoLink: <ImgPlaceholder src={row.logoUrl} loading="lazy" aria-hidden placeholder={placeholdersMap[TEAMLOGO]} />,
      teamLink: !row.teamUrl ? (
        <Fragment>{row.teamName}</Fragment>
      ) : (
        <Cta href={row.teamUrl} trackingData={trackingData} narrow uppercase={false} hasWrapper={false}>
          {row.teamName}
        </Cta>
      ),
    };

    if (teamId) {
      item.classRow = (_row: ICompetitionRankingRow) => _row.id === teamId;
    } else if (team) {
      item.classRow = (_row: ICompetitionRankingRow) =>
        team.map((_team) => _team.toLowerCase()).includes(_row.teamName.toLowerCase());
    }

    return item;
  });

  return <Table columns={columns} rows={_rows} className={className} showTitles={showTitles} />;
};

export default CompetitionRankingTable;
